import {SceneNodes} from "@cm/template-nodes"
import {mathIsEqual, setThreeObjectPart, ThreeObject} from "@template-editor/helpers/three-object"
import {EXRLoader, HDRLoader, Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {from, tap} from "rxjs"
import {ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {DEFAULT_FLOAT_TEXTURE_TYPE} from "@template-editor/helpers/three-utils"
import {anyDifference, objectDifferent, objectFieldsDifferent} from "@template-editor/helpers/change-detection"
import {extensionForContentType} from "@cm/utils"

export class ThreeEnvironment extends ThreeObject<SceneNodes.Environment> {
    protected override renderObject: THREE.Group = new THREE.Group() //Dummy object
    wasDisposed = false

    private environmentTexture: THREE.Texture | null = null
    private rotationMatrix: THREE.Matrix4 = new THREE.Matrix4()
    private intensityNode = TSL.uniform(1.0)
    private saturationNode = TSL.uniform(1.0)
    private clampNode = TSL.uniform(1000.0)

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)
        setThreeObjectPart(this.renderObject, this)
    }

    override setup(sceneNode: SceneNodes.Environment) {
        return anyDifference([
            objectDifferent(sceneNode.envData, this.parameters?.envData, undefined, (envData) => {
                const {sceneManagerService} = this.threeSceneManagerService

                const getLoaderForExtension = (extension: string) => {
                    switch (extension.toLowerCase()) {
                        case "exr":
                            return new EXRLoader().setDataType(DEFAULT_FLOAT_TEXTURE_TYPE)
                        case "hdr":
                            return new HDRLoader().setDataType(DEFAULT_FLOAT_TEXTURE_TYPE)
                        default:
                            throw new Error(`Unsupported environment map file extension: ${extension}`)
                    }
                }

                const getLoaderAndData = (): {
                    loader: THREE.DataTextureLoader
                    url: string
                } => {
                    if (envData.type === "dataObject") {
                        const {dataObject} = envData
                        const {mediaType, downloadUrl, originalFileName} = dataObject

                        const extension = (() => {
                            try {
                                return extensionForContentType(mediaType)
                            } catch (e) {
                                return originalFileName.split(".").pop() ?? "hdr"
                            }
                        })()

                        return {loader: getLoaderForExtension(extension), url: downloadUrl}
                    } else return {loader: getLoaderForExtension(envData.originalFileExtension), url: envData.url}
                }

                const loadEnvironmentMap = async (textureLoader: THREE.DataTextureLoader, url: string) => {
                    return new Promise<THREE.Texture>((resolve, reject) => {
                        textureLoader.load(
                            url,
                            (texture) => {
                                texture.mapping = THREE.EquirectangularReflectionMapping
                                resolve(texture)
                            },
                            undefined,
                            () => {
                                reject()
                            },
                        )
                    })
                }

                const {loader, url} = getLoaderAndData()

                sceneManagerService.addTask({
                    description: `loadEnvironmentMap(${url})`,
                    task: from(loadEnvironmentMap(loader, url)).pipe(
                        tap((texture) => {
                            if (this.wasDisposed) {
                                texture.dispose()
                                return
                            }

                            if (this.environmentTexture) this.environmentTexture.dispose()

                            this.environmentTexture = texture

                            const pmremNode = new ContextAwareRotatedPmremNode(this.environmentTexture, TSL.uniform(this.rotationMatrix))

                            this.threeSceneManagerService.$environmentNode.set(
                                TSL.saturation(TSL.min(pmremNode.mul(this.intensityNode), this.clampNode), this.saturationNode),
                            )

                            this.onAsyncUpdate()
                        }),
                    ),
                    critical: true,
                })
            }),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["rotation", "clampHighlights", "mirror", "intensity", "saturation"],
                (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") return mathIsEqual(valueA, valueB)
                    else return valueA === valueB
                },
                (sceneNode) => {
                    const {rotation, mirror, intensity, saturation, clampHighlights} = sceneNode
                    const degToRad = Math.PI / 180
                    this.rotationMatrix
                        .makeRotationFromEuler(
                            new THREE.Euler(-rotation.x * degToRad, -(mirror ? 180.0 - rotation.y : rotation.y) * degToRad, -rotation.z * degToRad, "XYZ"),
                        )
                        .multiply(mirror ? new THREE.Matrix4().makeScale(1, 1, -1) : new THREE.Matrix4().identity())
                    this.intensityNode.value = intensity
                    this.saturationNode.value = saturation / 100.0
                    this.clampNode.value = clampHighlights ?? 1000.0
                },
            ),
        ])
    }

    override dispose(final: boolean) {
        if (final) {
            this.threeSceneManagerService.$environmentNode.set(undefined)

            if (this.environmentTexture) {
                this.environmentTexture.dispose()
                this.environmentTexture = null
            }

            this.wasDisposed = true
        }
    }
}

class ContextAwareRotatedPmremNode extends THREE.PMREMNode {
    private readonly rotationMatrixUniform: THREE.Node<"mat4">

    constructor(texture: THREE.Texture, rotationMatrixUniform: THREE.Node<"mat4">) {
        super(texture, null, null)
        this.rotationMatrixUniform = rotationMatrixUniform
    }

    override setup(builder: THREE.NodeBuilder) {
        const getUV = builder.getContext().getUV as ((node: THREE.Node, builder: THREE.NodeBuilder) => THREE.Node<"vec3">) | undefined
        if (!getUV) throw new Error("ContextAwareRotatedPmremNode requires builder context to provide getUV function")
        const contextUV = getUV(this, builder)
        this.uvNode = contextUV.mul(this.rotationMatrixUniform)
        return super.setup(builder)
    }
}
